import ListGroupComponent from '@pelckmans/elementis/components/list-group';
import List from '@pelckmans/elementis/components/list';
import Tooltip from '@pelckmans/elementis/components/tooltip';
import InfoIcon from '@pelckmans/elementis/icons/info';
import SuccessIcon from '@pelckmans/elementis/icons/success';
import PendingIcon from '@pelckmans/elementis/icons/pending';
import FailureIcon from '@pelckmans/elementis/icons/failure';
import { useTranslation } from 'react-i18next';
import Pill from '@pelckmans/elementis/components/pill';
import { useMemo } from 'react';
import { Group, Member } from '../../../../../../api/groups';

type Props = {
  group: Group;
  members: Member[];
};

function ListGroup({ group, members }: Props) {
  const { t } = useTranslation();

  const acceptedMembers = useMemo(() => members.filter(member => member.status === 'accepted'), [members]);
  const pendingMembers = useMemo(() => members.filter(member => member.status === 'pending'), [members]);
  const declinedMembers = useMemo(() => members.filter(member => member.status === 'declined'), [members]);

  return (
    <ListGroupComponent>
      <List density="compact">
        <List.Header
          title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.ACCEPTED.TITLE')}
          pill={
            <Pill icon={<SuccessIcon data-testid="success-icon" />} prominence="success" size="sm">
              {String(group.totalAccepted)}
            </Pill>
          }
          titleTag="h3"
          tooltip={
            <Tooltip anchor={<InfoIcon data-testid="tooltip-accepted" />} truncation="wrapped">
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.ACCEPTED.TOOLTIP_P1')}</p>
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.ACCEPTED.TOOLTIP_P2')}</p>
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.ACCEPTED.TOOLTIP_P3')}</p>
            </Tooltip>
          }
        />
        {acceptedMembers.length === 0 && (
          <List.Item title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.ACCEPTED.EMPTY')} />
        )}
        {acceptedMembers.map(member => (
          <List.Item
            key={member.id}
            title={[member.lastName, member.firstName].join(' ')}
            subText={member.primaryEmail}
          />
        ))}
      </List>

      <List density="compact">
        <List.Header
          title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.PENDING.TITLE')}
          pill={
            <Pill icon={<PendingIcon data-testid="pending-icon" />} prominence="warning" size="sm">
              {String(group.totalPending)}
            </Pill>
          }
          titleTag="h3"
          tooltip={
            <Tooltip anchor={<InfoIcon data-testid="tooltip-pending" />} truncation="wrapped">
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.PENDING.TOOLTIP_P1')}</p>
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.PENDING.TOOLTIP_P2')}</p>
            </Tooltip>
          }
        />
        {pendingMembers.length === 0 && (
          <List.Item title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.PENDING.EMPTY')} />
        )}
        {pendingMembers.map(member => (
          <List.Item key={member.id} title={member.primaryEmail} />
        ))}
      </List>
      <List density="compact">
        <List.Header
          title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.DECLINED.TITLE')}
          pill={
            <Pill icon={<FailureIcon data-testid="failure-icon" />} prominence="danger" size="sm">
              {String(group.totalDeclined)}
            </Pill>
          }
          titleTag="h3"
          tooltip={
            <Tooltip anchor={<InfoIcon data-testid="tooltip-declined" />} truncation="wrapped">
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.DECLINED.TOOLTIP_P1')}</p>
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.DECLINED.TOOLTIP_P2')}</p>
              <p>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.DECLINED.TOOLTIP_P3')}</p>
            </Tooltip>
          }
        />
        {declinedMembers.length === 0 && (
          <List.Item title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.LIST_GROUP.DECLINED.EMPTY')} />
        )}
        {declinedMembers.map(member => (
          <List.Item key={member.id} title={member.primaryEmail} />
        ))}
      </List>
    </ListGroupComponent>
  );
}

export default ListGroup;
